import React, { useState } from 'react';
import { User, PresenceManual, PresenceStatus, handleAvatarError } from '../../types';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { ContextMenu } from '../ContextMenu';
import { PresenceDot } from '../PresenceDot';
import { PRESENCE_CHOICES, manualToChoice, STATUS_LABEL } from '../../utils/presence';
import { isTauri } from '../../utils/tauri';
import { IS_COARSE_POINTER } from '../../utils/pointer';
import { Home, MessageCircle, Bell, Users, Shield, HelpCircle, Settings, ScrollText, LogOut, Monitor, CalendarDays } from 'lucide-react';

/** 데스크톱 좌측 세로 rail의 탭. 모바일 MobileTab('home'|'chat')과 별개 타입 — 상호 참조 금지 */
export type DesktopTab = 'home' | 'chat' | 'calendar' | 'activity' | 'org';

interface DesktopNavRailProps {
  activeTab: DesktopTab;
  onTabChange: (tab: DesktopTab) => void;
  /** 탭별 뱃지 수 (chat=안읽음 합계, activity=알림함 미읽음) */
  badges?: Partial<Record<DesktopTab, number>>;
  currentUser: User;
  /**
   * 소켓 연결 여부. 끊긴 동안에는 남이 보는 내 상태가 사실상 오프라인이므로 점을 회색으로 둔다
   * (끊김 진단은 상단 연결 배너가 담당 — 예전엔 이 점이 빨강이었지만 '다른 용무 중'과 겹친다).
   */
  isConnected: boolean;
  /** 서버가 판정한 내 접속 상태 — 아바타 우하단 점 */
  myStatus: PresenceStatus;
  /** 내가 직접 고른 상태(null=자동). 팝오버 라디오의 체크 위치 */
  myPresenceManual: PresenceManual;
  /** 상태 선택. 저장은 이 콜백 안에서 서버까지 끝낸다 */
  onPresenceSelect?: (status: 'auto' | 'away' | 'busy') => void;
  onAdminConsoleClick?: () => void;
  onHelpClick?: () => void;
  onSettingsClick?: () => void;
  /** 이용 고지·동의 재열람(내 동의 내역·철회) */
  onRulesClick?: () => void;
  onLogout?: () => void;
  /**
   * 768px 미만에서도 보인다 — PC앱 컴팩트 배치(카톡 PC식 좁은 창)용. 기본은 `md:` 이상에서만 보인다.
   * 웹/PWA는 좁으면 하단 탭바(MobileTabBar)를 쓰므로 넘기지 않는다.
   */
  alwaysVisible?: boolean;
}

const TABS: { key: DesktopTab; label: string; icon: React.ElementType }[] = [
  { key: 'home', label: '홈', icon: Home },
  { key: 'chat', label: '대화방', icon: MessageCircle },
  { key: 'calendar', label: '캘린더', icon: CalendarDays },
  { key: 'activity', label: '알림', icon: Bell },
  { key: 'org', label: '온라인', icon: Users },
];

/**
 * 데스크톱 3컬럼의 1번째 컬럼: 세로 아이콘 rail (디스코드 서버바 + 카카오톡PC 아이콘바 하이브리드).
 * 탭 전환은 가운데 목록 패널만 스왑하고 우측 ChatWindow는 유지한다.
 * MobileTabBar는 모바일 전용(md:hidden)이라 재사용하지 않고 별도 구현.
 */
export const DesktopNavRail: React.FC<DesktopNavRailProps> = ({
  activeTab,
  onTabChange,
  badges,
  currentUser,
  isConnected,
  myStatus,
  myPresenceManual,
  onPresenceSelect,
  onAdminConsoleClick,
  onHelpClick,
  onSettingsClick,
  onRulesClick,
  onLogout,
  alwaysVisible = false,
}) => {
  // 아바타 클릭 팝오버 — 신규 컴포넌트 대신 기존 ContextMenu(header 지원) 재사용
  const [profileMenu, setProfileMenu] = useState<{ x: number; y: number } | null>(null);

  const openProfileMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setProfileMenu({ x: rect.right + 8, y: rect.top });
  };

  // 상태 3지선다를 메뉴 맨 위에 둔다. 구분선 대신 아이콘 모양으로 그룹을 낸다 —
  // ContextMenu의 화살표 키 이동이 items 배열 기준이라 비항목을 섞을 수 없다.
  const presenceItems = onPresenceSelect
    ? PRESENCE_CHOICES.map((choice) => ({
        key: `presence-${choice.value}`,
        label: choice.label,
        // icon은 ElementType이라 색 점을 그리는 컴포넌트를 그대로 넘길 수 있다
        icon: () => <PresenceDot status={choice.dot} className="w-2.5 h-2.5 shrink-0" />,
        checked: manualToChoice(myPresenceManual) === choice.value,
        onSelect: () => { onPresenceSelect(choice.value); setProfileMenu(null); },
      }))
    : [];

  const profileItems = [
    ...presenceItems,
    onSettingsClick && {
      key: 'settings',
      label: '프로필 설정',
      icon: Settings,
      onSelect: () => { onSettingsClick(); setProfileMenu(null); },
    },
    onRulesClick && {
      key: 'rules',
      label: '이용 고지·동의',
      icon: ScrollText,
      onSelect: () => { onRulesClick(); setProfileMenu(null); },
    },
    // PC 앱에서는 이미 설치된 상태라 숨긴다. 설치파일은 로그인한 사용자만 받을 수 있어
    // 로그인 화면이 아니라 여기가 유일한 진입점이다 (새 탭이라 대화 세션이 유지된다).
    // 터치 기기에서도 숨긴다 — rail은 768px 이상에서 뜨므로 아이패드는 모바일 설정 탭이 아니라
    // 이 팝오버로 메뉴를 만난다. 한쪽만 막으면 태블릿에 그대로 남는다.
    !isTauri() && !IS_COARSE_POINTER && {
      key: 'download',
      label: 'PC 버전 다운로드',
      icon: Monitor,
      onSelect: () => { window.open('/download', '_blank'); setProfileMenu(null); },
    },
    onLogout && {
      key: 'logout',
      label: '로그아웃',
      icon: LogOut,
      danger: true,
      onSelect: () => { onLogout(); setProfileMenu(null); },
    },
  ].filter(Boolean) as { key: string; label: string; icon: React.ElementType; danger?: boolean; checked?: boolean; onSelect: () => void }[];

  return (
    // 세로로 넘치면 스크롤(최소 높이 창 + 업데이트 배너일 때). 팝오버·툴팁은 포털이라 잘리지 않는다
    <nav className={`${alwaysVisible ? 'flex' : 'hidden md:flex'} w-16 shrink-0 min-h-0 overflow-y-auto no-scrollbar flex-col items-center bg-slate-200 border-r border-slate-300 py-3 gap-1 select-none`}>
      {TABS.map(({ key, label, icon: Icon }) => {
        const isActive = activeTab === key;
        const badge = badges?.[key] || 0;
        return (
          <div key={key} className="relative w-full flex justify-center">
            {/* 활성 인디케이터: 디스코드식 좌측 필 */}
            {isActive && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-brand-600" />
            )}
            <button
              onClick={() => onTabChange(key)}
              title={label}
              aria-label={badge > 0 ? `${label}, 안읽음 ${badge}` : label}
              aria-current={isActive ? 'page' : undefined}
              data-tooltip-placement="right"
              className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                isActive
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-slate-500 hover:bg-slate-300/60 hover:text-slate-700'
              }`}
            >
              <Icon size={24} />
              {badge > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[11px] font-bold flex items-center justify-center leading-none">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          </div>
        );
      })}

      <div className="flex-1" />

      {currentUser.isAdmin && onAdminConsoleClick && (
        <button
          onClick={onAdminConsoleClick}
          title="관리자 콘솔"
          aria-label="관리자 콘솔"
          data-tooltip-placement="right"
          className="w-12 h-12 rounded-xl flex items-center justify-center text-amber-500 hover:bg-slate-300/60 hover:text-amber-600 transition-colors"
        >
          <Shield size={24} />
        </button>
      )}
      {onHelpClick && (
        <button
          onClick={onHelpClick}
          title="도움말"
          aria-label="도움말"
          data-tooltip-placement="right"
          className="w-12 h-12 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-300/60 hover:text-slate-700 transition-colors"
        >
          <HelpCircle size={24} />
        </button>
      )}
      <button
        onClick={openProfileMenu}
        title={`${currentUser.username} · ${STATUS_LABEL[isConnected ? myStatus : 'offline']}`}
        aria-label={`${currentUser.username} 프로필 메뉴, 내 상태 ${STATUS_LABEL[isConnected ? myStatus : 'offline']}`}
        aria-haspopup="menu"
        data-tooltip-placement="right"
        className="relative mt-1 rounded-full hover:ring-2 hover:ring-brand-400/60 transition-shadow"
      >
        <img
          src={resolveAvatarUrl(currentUser.avatar)} onError={handleAvatarError}
          alt={currentUser.username}
          className="w-10 h-10 rounded-full object-cover bg-slate-100 border-2 border-slate-200"
        />
        {/* 내 접속 상태점. 소켓이 끊기면 남에게도 오프라인으로 보이므로 회색이 맞다 */}
        <PresenceDot
          status={isConnected ? myStatus : 'offline'}
          className="absolute bottom-0 right-0 w-3 h-3 border-2 border-slate-200"
        />
      </button>

      {profileMenu && profileItems.length > 0 && (
        <ContextMenu
          x={profileMenu.x}
          y={profileMenu.y}
          variant="light"
          onClose={() => setProfileMenu(null)}
          header={
            <div className="px-1 py-1">
              <p className="text-sm font-semibold text-slate-800 truncate">{currentUser.username}</p>
              <p className="text-xs text-slate-400 truncate">{currentUser.depart || ' '}</p>
            </div>
          }
          items={profileItems}
        />
      )}
    </nav>
  );
};
